import * as React from 'react';
import { observer } from 'mobx-react';

import { useDebouncedEffect } from 'shared/entities/common/hooks';
import {
  SelectorKey,
  SelectorType,
  SelectorProps,
  SelectorSize
} from 'shared/newEntities/components/Selector';
import { SortDirection } from 'shared/newEntities/common';
import {
  renderTranslationString,
  useTypedTranslation
} from 'shared/entities/localization';
import SelectorBase from 'shared/newComponents/SelectorBase';
import SelectorPopupSearch from 'shared/newComponents/SelectorPopupSearch';
import { TypographySize } from 'shared/newEntities/components/Typography';

import SelectedOption from './SelectedOption';
import Content from './Content';
import Trigger from './Trigger';

import './Selector.modules.scss';

const DEBOUNCE_DELAY = 500;

type Props = SelectorProps & {
  externalSearch?: {
    value: string;
    onChange: (value: string) => void;
    clearOnClose?: boolean;
  };
};

const Selector: React.FC<Props> = (props: Props) => {
  const { t, lng } = useTypedTranslation();

  const {
    placeholder,
    pluralize,
    loadingStage,
    options,
    onClose,
    title,
    className,
    TriggerIcon,
    onBlur,
    size = SelectorSize.large,
    triggerFontWeight,
    triggerFontColor,
    externalSearch,
    contentTopNode,
    popupSearch,
    withoutSearch,
    emptyText,
    withStub,
    stablePopupSize,
    renderTrigger,
    hoverReferencePosition,
    disabled,
    withRemovingInTrigger,
    ...rest
  } = props;
  const [internalSearchText, setInternalSearchText] = React.useState('');

  // Используем внешний searchValue если он передан, иначе внутреннее состояние
  const searchText =
    externalSearch?.value !== undefined
      ? externalSearch.value
      : internalSearchText;
  const [internalActualOptionsIds, setInternalActualOptionsIds] =
    React.useState<SelectorKey[]>(() => [...options.keys]);

  const entities = React.useMemo(() => {
    return Object.keys(options.entities).reduce((acc, key) => {
      const item = options.entities[key];

      if (!item) {
        return acc;
      }

      return {
        ...acc,
        [key]: {
          ...item,
          title: renderTranslationString(item.title, t),
          description: item.description
            ? renderTranslationString(item.description, t)
            : item.description,
          id: item.id
        }
      };
    }, {});
  }, [lng, options.entities, options.keys, options.keys.length]);

  const sortOrder =
    props.sortOrder === null
      ? null
      : props.sortOrder
        ? props.sortOrder
        : SortDirection.asc;

  // С внешним поиском родитель сразу отдаёт `options` под строку. Деривим
  // список ключей в render — иначе sync через `useEffect` (как в filterOptions)
  // срабатывает на кадр позже и в Content получается «дырка» между списком
  // и плейсхолдером (старые id без entity).
  const optionKeysFromParent = React.useMemo((): SelectorKey[] => {
    if (!sortOrder) {
      return options.keys;
    }
    return [...options.keys].sort((firstId, secondId) => {
      const firstMoreThanSecond =
        entities[firstId]?.title.toLowerCase?.() >
        entities[secondId]?.title.toLowerCase?.();

      if (sortOrder === SortDirection.asc) {
        return firstMoreThanSecond ? 1 : -1;
      }
      return firstMoreThanSecond ? -1 : 1;
    });
  }, [sortOrder, options.keys, options.keys.length, entities]);

  const actualOptionsIds =
    externalSearch || !searchText
      ? optionKeysFromParent
      : internalActualOptionsIds;

  const filterOptions = React.useCallback(() => {
    if (externalSearch) {
      return;
    }
    let ids: SelectorKey[] = [];

    if (sortOrder) {
      ids = [...options.keys].sort((firstId, secondId) => {
        const firstMoreThanSecond =
          entities[firstId]?.title.toLowerCase?.() >
          entities[secondId]?.title.toLowerCase?.();

        if (sortOrder === SortDirection.asc) {
          return firstMoreThanSecond ? 1 : -1;
        }
        return firstMoreThanSecond ? -1 : 1;
      });
    } else {
      ids = options.keys;
    }

    setInternalActualOptionsIds(
      ids.filter((item) => {
        const option = entities[item];

        return option
          ? String(option.title)
              .toLowerCase()
              .includes(searchText.toLowerCase())
          : false;
      })
    );
  }, [searchText, sortOrder, entities, options.keys, externalSearch]);

  const handleOptionClick = React.useCallback(
    (id: SelectorKey) => {
      if (entities[id]?.disabled) {
        return;
      }

      if (props.type !== SelectorType.multi) {
        props.onChange(id);
        return;
      }

      if (props.value.includes(id)) {
        props.onChange(props.value.filter((selectedId) => selectedId !== id));
      } else {
        if (props.saveKeysOrder) {
          const selectedIds = [id, ...props.value];
          props.onChange(options.keys.filter((id) => selectedIds.includes(id)));
        } else {
          props.onChange([id, ...props.value]);
        }
      }
    },
    [props.onChange, props.value, props.type, entities]
  );

  const handleRemove = React.useCallback(() => {
    if (props.type === SelectorType.multi) {
      if (withRemovingInTrigger) {
        props.onChange([]);
      }
      return;
    }

    props.onChange(null);
  }, [props.onChange, props.type, withRemovingInTrigger]);

  // Для внешнего поиска дебаунс не нужен, так как он управляется извне
  // Для внутреннего поиска используем дебаунс
  useDebouncedEffect(
    () => {
      if (!externalSearch) {
        filterOptions();
      }
    },
    DEBOUNCE_DELAY,
    [searchText]
  );

  // Обработчик изменения поиска
  const handleSearchChange = React.useCallback(
    (value: string) => {
      if (externalSearch) {
        externalSearch.onChange(value);
      } else {
        setInternalSearchText(value);
      }
    },
    [externalSearch]
  );

  const handleClosePopup = React.useCallback(() => {
    if (externalSearch) {
      externalSearch.clearOnClose && externalSearch.onChange('');
    } else {
      setInternalSearchText('');
    }
    onBlur?.();
    onClose?.();
  }, [onClose, onBlur, handleSearchChange]);

  const selectedAll = React.useMemo((): boolean | null => {
    if (props.type === SelectorType.single) {
      return null;
    }

    return !actualOptionsIds.some((id) => !props.value.includes(id));
  }, [actualOptionsIds, props.value]);

  const hasSelected =
    props.type === SelectorType.multi && props.value.length > 0;

  const shouldClear = hasSelected || !!selectedAll;

  const handleSelectAllOrClear = React.useCallback(() => {
    if (props.type === SelectorType.single) {
      return;
    }
    props.onChange(shouldClear ? [] : actualOptionsIds);
  }, [props.type, actualOptionsIds, shouldClear]);

  const withSelectedAll =
    props.type === SelectorType.multi &&
    props.showSelectedAll &&
    selectedAll !== null &&
    !!actualOptionsIds.length;

  const selectedAllOptions = React.useMemo(() => {
    if (!withSelectedAll) {
      return undefined;
    }
    return {
      handler: handleSelectAllOrClear,
      shouldClear
    };
  }, [withSelectedAll, handleSelectAllOrClear, shouldClear]);

  const singleEntity =
    rest.type === SelectorType.single && rest.value
      ? options.entities[rest.value]
      : null;

  const actualOptions = {
    keys: actualOptionsIds,
    entities
  };

  const actualTriggerFontSize =
    size === SelectorSize.small ? TypographySize.xs : TypographySize.m;

  const effectiveWithoutSearch = withoutSearch || !!popupSearch;

  const renderContentTopNode = React.useCallback(
    (onClose: () => void) => {
      const userNode =
        typeof contentTopNode === 'function'
          ? contentTopNode(onClose)
          : contentTopNode;

      if (!popupSearch && !userNode) {
        return null;
      }

      const searchNode = popupSearch ? (
        <SelectorPopupSearch
          value={searchText}
          onChange={handleSearchChange}
          placeholder={popupSearch.placeholder}
        />
      ) : null;

      return (
        <>
          {userNode}
          {searchNode}
        </>
      );
    },
    [contentTopNode, popupSearch, searchText, handleSearchChange]
  );

  return (
    <SelectorBase
      {...rest}
      disabled={disabled}
      onClose={handleClosePopup}
      className={className}
      optionsLength={actualOptionsIds.length}
      stablePopupSize={stablePopupSize}
      size={size}
      renderTrigger={renderTrigger}
      trigger={(isOpen) => (
        <Trigger
          options={actualOptions}
          isSelectorOpened={isOpen}
          searchText={searchText}
          onChangeSearchText={handleSearchChange}
          pluralize={pluralize}
          placeholder={placeholder}
          title={title}
          Icon={TriggerIcon}
          fontSize={actualTriggerFontSize}
          fontWeight={triggerFontWeight}
          fontColor={triggerFontColor}
          selectorSize={size}
          withoutSearch={effectiveWithoutSearch}
          onRemove={withRemovingInTrigger ? handleRemove : undefined}
          {...rest}
        />
      )}
      contentTopNode={
        !disabled &&
        rest.type === SelectorType.single &&
        rest.withRemoving !== false &&
        singleEntity && (
          <SelectedOption {...singleEntity} onRemove={handleRemove} />
        )
      }
    >
      {(onClose) => (
        <Content
          {...rest}
          emptyText={emptyText}
          withStub={withStub}
          stablePopupSize={stablePopupSize}
          onClose={onClose}
          options={actualOptions}
          loadingStage={loadingStage}
          onOptionClick={handleOptionClick}
          selectorSize={size}
          topNode={renderContentTopNode(onClose)}
          hoverReferencePosition={hoverReferencePosition}
          listResetKey={searchText}
          {...(rest.type === SelectorType.multi ? { selectedAllOptions } : {})}
        />
      )}
    </SelectorBase>
  );
};

export default observer(Selector);
